import { useState, useCallback, useEffect } from 'react';
import { Project, DatabaseProject } from '../types/project';
import { fetchAllProjects, addNewProject, updateProject, deleteProject } from '../services/projectService';
import { useToast } from '@/hooks/use-toast';
import { mapDatabaseProjectToProject } from '@/utils/projectMappers';

const LOCAL_STORAGE_KEY = 'construction-app-projects';

export const useProjectData = () => {
  const { toast } = useToast();
  
  // Initialize with empty projects array
  const [projects, setProjects] = useState<Project[]>([]);
  
  // Track current site office to detect changes
  const [currentSiteOffice, setCurrentSiteOffice] = useState<string | null>(
    localStorage.getItem('selectedSiteOffice')
  );

  // Check if current user is an administrator
  const isCurrentUserAdmin = useCallback((): boolean => {
    const teamMemberStr = localStorage.getItem('currentTeamMember');
    if (!teamMemberStr) return false;
    try {
      const teamMember = JSON.parse(teamMemberStr);
      const role = (teamMember.role || '').toLowerCase();
      return role === 'administrator' || role === 'admin' || teamMember.is_admin === true;
    } catch {
      return false;
    }
  }, []);

  // Load projects from the database
  const loadProjects = useCallback(async (showToast: boolean = true) => {
    try {
      const siteOffice = localStorage.getItem('selectedSiteOffice');
      const siteOfficeId = localStorage.getItem('selectedSiteOfficeId');
      const isAdmin = isCurrentUserAdmin();
      
      // Allow admin to load all projects even without site office selected
      if (!siteOffice && !siteOfficeId && !isAdmin) {
        console.log('No site office selected and not admin, clearing projects');
        setProjects([]);
        return [];
      }

      console.log(`Loading projects ${siteOffice ? `for site office: ${siteOffice}` : '(admin - all projects)'}`);
      const databaseProjects = await fetchAllProjects();
      
      // Validate and clean up projects before setting state
      const validProjects = databaseProjects.filter(project => {
        if (!project || !project.id || !project.name) {
          console.warn('Invalid project data:', project);
          return false;
        }
        return true;
      });
      
      // Deduplicate by id
      const uniqueProjects = validProjects.reduce((acc, project) => {
        const exists = acc.find(p => p.id === project.id);
        if (!exists) {
          acc.push(project);
        }
        return acc;
      }, [] as Project[]);
      
      console.log(`Valid unique projects loaded:`, uniqueProjects.length);
      console.log('Project details:', uniqueProjects.map(p => ({
        id: p.id,
        name: p.name,
        projectType: p.projectType,
        materialsCount: p.materials?.length || 0,
        laborCount: p.labor?.length || 0
      })));
      
      // Update the state with validated unique projects
      setProjects(uniqueProjects);
      
      // Only show toast on initial load, not on refresh triggers
      if (showToast) {
        if (validProjects.length === 0) {
          toast({
            title: "No Projects Found",
            description: siteOffice ? `No projects found for ${siteOffice}.` : "No projects found.",
            variant: "default"
          });
        }
      }
      
      return databaseProjects;
    } catch (err) {
      console.error('Error loading projects:', err);
      toast({
        title: "Error Loading Projects",
        description: "Failed to load projects from database. Please try again.",
        variant: "destructive"
      });
      return [];
    }
  }, [toast, isCurrentUserAdmin]);

  // Monitor site office changes and reload projects accordingly
  useEffect(() => {
    const siteOffice = localStorage.getItem('selectedSiteOffice');
    const isAdmin = isCurrentUserAdmin();
    
    if (siteOffice !== currentSiteOffice) {
      console.log(`Site office changed from ${currentSiteOffice} to ${siteOffice}`);
      setCurrentSiteOffice(siteOffice);
      
      // Load projects if site office selected OR if admin
      if (siteOffice || isAdmin) {
        loadProjects();
      } else {
        setProjects([]);
      }
    }
  }, [currentSiteOffice, loadProjects, isCurrentUserAdmin]);

  // Initial load when hook is first used
  useEffect(() => {
    const siteOffice = localStorage.getItem('selectedSiteOffice');
    const isAdmin = isCurrentUserAdmin();
    
    // Load if site office selected OR admin
    if (siteOffice || isAdmin) {
      loadProjects();
    }
  }, [loadProjects, isCurrentUserAdmin]);
  
  // Function to sync projects with the database
  const syncWithDatabase = useCallback(async () => {
    try {
      const loadedProjects = await loadProjects();
      
      if (loadedProjects && loadedProjects.length > 0) {
        console.log('Found database projects:', loadedProjects);
        
        setProjects(loadedProjects);
        
        toast({
          title: "Synced with Database",
          description: `${loadedProjects.length} projects synced from database`,
        });
      }
    } catch (err) {
      console.error('Error connecting to database:', err);
    }
  }, [toast, loadProjects]);

  // Add a new project
  const addProject = useCallback(async (project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) => {
    const siteOffice = localStorage.getItem('selectedSiteOffice');
    if (!siteOffice) {
      toast({
        title: "No Site Office Selected",
        description: "Please select a site office before creating a project.",
        variant: "destructive"
      });
      return null;
    }

    // Complete the project object with missing properties
    const completeProject = {
      ...project,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    try {
      // Add project to database
      const newProject = await addNewProject(completeProject);

      if (newProject) {
        // Deduplicate: only add if not already in the list
        setProjects(prev => {
          const exists = prev.some(p => p.id === newProject.id);
          if (exists) {
            return prev.map(p => p.id === newProject.id ? newProject : p);
          }
          return [...prev, newProject];
        });
        toast({
          title: "Project Created",
          description: `Project "${project.name}" has been created successfully in ${siteOffice}.`,
        });
        return newProject;
      }

      toast({
        title: "Error Creating Project",
        description: "Gagal menyimpan project ke database. Silakan coba lagi.",
        variant: "destructive"
      });
      return null;
    } catch (err) {
      console.error('Error creating project:', err);
      toast({
        title: "Error Creating Project",
        description: err instanceof Error ? err.message : "Gagal menyimpan project ke database.",
        variant: "destructive"
      });
      return null;
    }
  }, [toast, setProjects]);


  // Update a project by ID
  const updateProjectById = useCallback(async (id: string, updatedProject: Partial<Project>) => {
    const updated = await updateProject(id, updatedProject);
    if (updated) {
      setProjects(prev =>
        prev.map(project => (project.id === id ? { ...project, ...updated, isLocalOnly: false } : project))
      );
      return updated;
    }
    return null;
  }, []);

  // Get a project by ID
  const getProject = useCallback((id: string) => {
    return projects.find(project => project.id === id);
  }, [projects]);

  // Delete a project by ID
  const deleteProjectById = useCallback(async (id: string) => {
    const success = await deleteProject(id);
    if (success) {
      setProjects(prev => prev.filter(project => project.id !== id));
      toast({
        title: "Project Deleted",
        description: "The project has been successfully deleted.",
      });
      return true;
    }
    toast({
      title: "Error Deleting Project",
      description: "There was a problem deleting the project. Please try again.",
      variant: "destructive"
    });
    return false;
  }, [toast]);

  // Delete local-only projects (not relevant anymore since we're using site office filtering)
  const deleteLocalOnlyProjects = useCallback(() => {
    toast({
      title: "No Local Projects",
      description: "All projects are now managed by site office.",
    });
  }, [toast]);

  // Save projects to localStorage (not relevant anymore but keeping for compatibility)
  const saveProjectsToLocalStorage = useCallback(() => {
    // No longer needed as projects are stored by site office in database
  }, []);
  
  return {
    projects,
    setProjects,
    loadProjects,
    syncWithDatabase,
    addProject,
    updateProject: updateProjectById,
    getProject,
    deleteProject: deleteProjectById,
    deleteLocalOnlyProjects,
    saveProjectsToLocalStorage
  };
};
